import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { Button } from '../../../components/shared/Button';
import { useLanguage } from '../../../hooks/shared';
import {
  notificationStore,
  useEventInstanceStore,
  useUserStore,
  useWorkspaceStore,
} from '../../../stores';
import { Training } from '../types/calendarTypes';
import { ParticipantActionsStore } from './ParticipantActionsStore';

interface ParticipantActionsProps {
  training: Training;
}

const ParticipantActions: React.FC<ParticipantActionsProps> = observer(
  ({ training }) => {
    const { t } = useLanguage();
    const userStore = useUserStore();
    const workspaceStore = useWorkspaceStore();
    const eventInstanceStore = useEventInstanceStore();

    const currentUserId = userStore.userId || '';

    // Create local store
    const [store] = useState(
      () =>
        new ParticipantActionsStore(
          training,
          currentUserId,
          userStore,
          workspaceStore,
          eventInstanceStore,
          notificationStore
        )
    );

    // Update store when training or currentUserId changes
    useEffect(() => {
      store.training = training;
      store.currentUserId = currentUserId;
    }, [store, training, currentUserId]);

    // Show replace button if user is not a participant and can replace someone
    if (store.shouldShowReplaceButton) {
      return (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200">
          <Button
            variant="outline"
            size="sm"
            aria-disabled={!store.canReplace}
            className={
              !store.canReplace ? 'opacity-60 pointer-events-auto' : ''
            }
            onClick={async (e) => {
              e.stopPropagation();
              await store.handleReplaceParticipant();
            }}
          >
            {t('calendar.replaceParticipant')}
          </Button>
        </div>
      );
    }

    // No actions needed
    if (!store.needsAnyAction) {
      return null;
    }

    return (
      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200">
        {store.needsConfirmationAction && (
          <Button
            variant="outline"
            size="sm"
            aria-disabled={store.isReplacedStatus}
            className={
              store.isReplacedStatus ? 'opacity-60 pointer-events-auto' : ''
            }
            onClick={(e) => {
              e.stopPropagation();
              if (store.isReplacedStatus) {
                notificationStore.showInfo(t('calendar.youWereReplaced'));
                return;
              }

              if (
                !store.canConfirmNowForThisUser &&
                !store.canConfirmAfterCancellation
              ) {
                notificationStore.showInfo(t('calendar.alreadyConfirmed'));
                return;
              }

              if (store.currentUserParticipant) {
                store.handleConfirm(store.currentUserParticipant.id);
              }
            }}
          >
            {t('calendar.confirmParticipation')}
          </Button>
        )}

        {store.canCancelParticipation && (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              store.handleCancelParticipation();
            }}
            className="flex-shrink-0 text-orange-600 border-orange-300 hover:bg-orange-50"
          >
            {t('training.cancelParticipation')}
          </Button>
        )}

        {store.canCancelReplacement && (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              store.handleCancelParticipation();
            }}
            className="flex-shrink-0 text-orange-600 border-orange-300 hover:bg-orange-50"
          >
            {t('training.cancelParticipation')}
          </Button>
        )}
      </div>
    );
  }
);

export default ParticipantActions;
